import { useMemo, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { CmsApiClient } from "../lib/api";
import { t } from "../lib/i18n";
import { ArticleEditor } from "../pages/ArticleEditor";
import { ArticleList } from "../pages/ArticleList";
import { Comments } from "../pages/Comments";
import { Dashboard } from "../pages/Dashboard";
import { Deployments } from "../pages/Deployments";
import { GitHubSettings } from "../pages/GitHubSettings";
import { LlmSettings } from "../pages/LlmSettings";
import { MediaLibrary } from "../pages/MediaLibrary";
import { PageList } from "../pages/PageList";
import { Taxonomy } from "../pages/Taxonomy";
import { ThemeSettings } from "../pages/ThemeSettings";
import { ApiProvider } from "./ApiContext";
import { Shell } from "./Shell";

/**
 * The Admin application (SPEC §38).
 *
 * The route table mirrors the SPEC's list. Routes whose pages are not built yet
 * are not registered — a menu entry that leads to a blank screen is worse than
 * one that says "soon".
 */
export function App() {
  /**
   * Set when the API answers 401.
   *
   * Held here rather than in each page, because "the session expired" is one
   * fact about the app, and every page discovering it independently would mean
   * every page rendering its own version of the same message.
   */
  const [unauthenticated, setUnauthenticated] = useState(false);

  const client = useMemo(
    () =>
      new CmsApiClient({
        // Same-origin in production; VITE_API_BASE_URL exists so a dev server
        // (and the E2E suite) can point the static admin at another Worker.
        ...(import.meta.env.VITE_API_BASE_URL === undefined
          ? {}
          : { baseUrl: import.meta.env.VITE_API_BASE_URL }),
        onUnauthorized: () => {
          setUnauthenticated(true);
        },
      }),
    [],
  );

  if (unauthenticated) {
    return <SignInPrompt />;
  }

  return (
    <ApiProvider client={client}>
      <BrowserRouter>
        <Routes>
          <Route element={<Shell />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="articles" element={<ArticleList />} />
            {/* Keyed so the first save — which replaces the URL with the new
                entry's canonical one — remounts the editor on the real draft. */}
            <Route path="articles/new" element={<ArticleEditor key="new" />} />
            <Route path="articles/:entryId/:locale" element={<ArticleEditor />} />
            <Route path="pages" element={<PageList />} />
            <Route path="taxonomy" element={<Taxonomy />} />
            <Route path="media" element={<MediaLibrary />} />
            <Route path="comments" element={<Comments />} />
            <Route path="deployments" element={<Deployments />} />
            <Route path="themes" element={<ThemeSettings />} />
            <Route path="settings/github" element={<GitHubSettings />} />
            <Route path="settings/llm" element={<LlmSettings />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ApiProvider>
  );
}

/**
 * Shown when the API rejects the session.
 *
 * No sign-in form: authentication is Cloudflare Access, which happens *before*
 * the Admin is served at all (SPEC §58). By the time this renders, the way back
 * is to re-authenticate with Access, not to type a password here.
 */
function SignInPrompt() {
  const [secret, setSecret] = useState("");

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-8">
      <h1 className="text-xl font-semibold text-neutral-900">{t("signin.title")}</h1>
      <p className="max-w-md text-center text-sm text-neutral-600">{t("signin.body")}</p>
      <form
        className="flex w-full max-w-md items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();

          if (secret !== "") {
            localStorage.setItem("cms-dev-auth-secret", secret);
          }

          window.location.reload();
        }}
      >
        <input
          type="password"
          className="h-9 w-full rounded-md border border-neutral-300 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
          placeholder={t("signin.placeholder")}
          aria-label={t("signin.aria")}
          value={secret}
          onChange={(event) => {
            setSecret(event.target.value);
          }}
        />
        <button
          type="submit"
          className="shrink-0 rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white"
        >
          {t("signin.button")}
        </button>
      </form>
    </main>
  );
}

function NotFound() {
  return (
    <div className="rounded-lg border border-dashed border-neutral-300 p-10 text-center">
      <p className="text-sm font-medium text-neutral-700">Page not found</p>
      <p className="mt-1 text-sm text-neutral-500">
        The Admin has no route for this URL.
      </p>
    </div>
  );
}
